import { useState } from "react";
import { motion } from "framer-motion";
import { Package, Search, Clock, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderLookupPage = () => {
  const [email, setEmail] = useState("");
  const [orderId, setOrderId] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);
    setError("");
    setSearched(true);

    try {
      const params = new URLSearchParams({ email: email.trim() });
      if (orderId.trim()) {
        params.set("order_id", orderId.trim());
      }
      const res = await axios.get(`${API}/orders/lookup/by-email?${params.toString()}`);
      setOrders(res.data.orders || []);
    } catch (err) {
      console.error("Lookup error:", err);
      setError(err.response?.data?.detail || "Unable to lookup orders. Please try again.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "ready":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "pending":
      case "processing":
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case "cancelled":
      case "failed":
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Package className="w-5 h-5 text-primary" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "ready":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "pending":
      case "processing":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
      case "cancelled":
      case "failed":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      default:
        return "bg-primary/10 text-primary border-primary/20";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen pt-20" data-testid="order-lookup-page">
      {/* Header */}
      <section className="py-12 bg-card border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Package className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-unbounded text-3xl md:text-4xl font-bold text-foreground mb-2">
              Track Your Order
            </h1>
            <p className="font-outfit text-muted-foreground">
              Enter your email to view your recent orders and pickup status
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lookup Form */}
      <section className="py-12">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onSubmit={handleLookup}
            className="bg-card border border-border rounded-2xl p-6 space-y-4"
            data-testid="order-lookup-form"
          >
            <div className="space-y-2">
              <Label htmlFor="email" className="font-outfit">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-muted border-border"
                data-testid="lookup-email-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="orderId" className="font-outfit">
                Order ID <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="orderId"
                type="text"
                placeholder="e.g., abc12345"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                className="bg-muted border-border"
                data-testid="lookup-orderid-input"
              />
              <p className="text-xs text-muted-foreground font-outfit">
                Enter the first 8 characters of your order ID to find a specific order
              </p>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm font-outfit" data-testid="lookup-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="lookup-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Looking up...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Find My Orders
                </>
              )}
            </Button>
          </motion.form>
        </div>
      </section>

      {/* Results */}
      {searched && !loading && (
        <section className="pb-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {orders.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
                data-testid="no-orders-found"
              >
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="font-outfit text-muted-foreground">
                  No orders found for this email address.
                </p>
                <p className="font-outfit text-sm text-muted-foreground mt-2">
                  Make sure you entered the same email used during checkout.
                </p>
              </motion.div>
            ) : (
              <div className="space-y-4">
                <p className="font-outfit text-sm text-muted-foreground mb-4" data-testid="orders-count">
                  Found {orders.length} {orders.length === 1 ? "order" : "orders"}
                </p>

                {orders.map((order, index) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-card border border-border rounded-2xl overflow-hidden"
                    data-testid={`order-card-${order.id}`}
                  >
                    {/* Order Header */}
                    <div className="p-4 sm:p-6 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <p className="font-outfit text-xs text-muted-foreground uppercase tracking-wider mb-1">
                          Order ID
                        </p>
                        <p className="font-unbounded text-foreground font-semibold">
                          #{order.id?.slice(0, 8).toUpperCase()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-outfit border ${getStatusColor(order.status)}`}>
                          {getStatusIcon(order.status)}
                          {order.status || "Processing"}
                        </span>
                      </div>
                    </div>

                    {/* Order Details */}
                    <div className="p-4 sm:p-6">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="font-outfit text-xs text-muted-foreground mb-1">Date</p>
                          <p className="font-outfit text-sm text-foreground">{formatDate(order.created_at)}</p>
                        </div>
                        <div>
                          <p className="font-outfit text-xs text-muted-foreground mb-1">Total</p>
                          <p className="font-outfit text-sm text-foreground font-semibold">${order.total?.toFixed(2)}</p>
                        </div>
                        {order.pickup_time && (
                          <div>
                            <p className="font-outfit text-xs text-muted-foreground mb-1">Pickup Time</p>
                            <p className="font-outfit text-sm text-foreground">{order.pickup_time}</p>
                          </div>
                        )}
                      </div>

                      {/* Items */}
                      {order.items && order.items.length > 0 && (
                        <div className="border-t border-border pt-4 mt-4">
                          <p className="font-outfit text-xs text-muted-foreground uppercase tracking-wider mb-3">
                            Items ({order.items.length})
                          </p>
                          <div className="space-y-2">
                            {order.items.map((item, i) => (
                              <div key={i} className="flex items-center justify-between text-sm font-outfit">
                                <span className="text-foreground">
                                  {item.name || item.product_name} × {item.quantity}
                                </span>
                                <span className="text-muted-foreground">
                                  ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default OrderLookupPage;
